/**
 * Выбор места съёмки на карте: точкой или областью.
 *
 * У исторического снимка точку назвать часто нельзя — известен квартал или
 * берег реки. Тогда модератор очерчивает многоугольник, и попадание внутрь
 * засчитывается игроку как точный ответ; промах считается от ближайшей
 * границы, а не от центра. Раньше это умела только форма создания квеста
 * в самой игре, и панель модерации области не видела и стирала бы её.
 *
 * Координаты можно вбить и числами, но по карте это делается быстрее и без
 * ошибок в знаках. Стиль светлый — тот же, что в игре: на тёмной карте
 * подписи городов читаются плохо, а модератор по ним и ставит точку.
 */
import { useState } from "react";
import { Button, InputNumber, Segmented, Space, Typography } from "antd";
import Map, { Layer, Marker, NavigationControl, Source } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import yandexLogo from "../assets/yandex-logo.svg";
import { mapStyle, usesYandexTiles } from "../lib/mapStyle";

const MAP_STYLE = mapStyle();

interface Value {
  latitude: number;
  longitude: number;
}

/** Меньше трёх вершин — не многоугольник, а отрезок. */
const MIN_AREA_POINTS = 3;

interface Props {
  value?: Value | null;
  onChange?: (value: Value) => void;
  /** Область ответа. Пустая — ответом служит точка. */
  area?: Value[];
  onAreaChange?: (area: Value[]) => void;
  height?: number;
}

/** Кольцо GeoJSON: первая точка повторяется последней. */
function polygon(area: Value[]) {
  const ring = area.map((p) => [p.longitude, p.latitude]);
  ring.push(ring[0]);
  return {
    type: "FeatureCollection" as const,
    features: [
      {
        type: "Feature" as const,
        properties: {},
        geometry: { type: "Polygon" as const, coordinates: [ring] },
      },
    ],
  };
}

export function LocationPicker({
  value,
  onChange,
  area = [],
  onAreaChange,
  height = 340,
}: Props) {
  const [point, setPoint] = useState<Value | null>(value ?? null);
  // Режим выводится из данных: у записи с областью он и должен быть «область»,
  // без лишнего клика при открытии на правку.
  const [mode, setMode] = useState<"point" | "area">(area.length > 0 ? "area" : "point");

  // Значение приходит из формы Ant Design (например, при загрузке записи на
  // редактирование). Синхронизируем во время рендера, а не эффектом, чтобы
  // не показывать кадр со старой меткой.
  const [syncedValue, setSyncedValue] = useState(value ?? null);
  if (
    value &&
    (value.latitude !== syncedValue?.latitude || value.longitude !== syncedValue?.longitude)
  ) {
    setSyncedValue(value);
    setPoint(value);
  }

  const update = (next: Value) => {
    setPoint(next);
    onChange?.(next);
  };

  const drawing = mode === "area" && Boolean(onAreaChange);
  const shape = area.length >= MIN_AREA_POINTS ? polygon(area) : null;

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={12}>
      <div style={{ position: "relative", height, borderRadius: 8, overflow: "hidden" }}>
        <Map
          initialViewState={{
            longitude: point?.longitude ?? 20,
            latitude: point?.latitude ?? 40,
            zoom: point ? 6 : 1.4,
          }}
          mapStyle={MAP_STYLE}
          style={{ width: "100%", height: "100%" }}
          dragRotate={false}
          cursor="crosshair"
          onClick={(event) => {
            const next = { latitude: event.lngLat.lat, longitude: event.lngLat.lng };
            if (drawing) {
              onAreaChange?.([...area, next]);
              return;
            }
            update(next);
          }}
        >
          <NavigationControl position="top-right" showCompass={false} />

          {shape && (
            <Source id="answer-area" type="geojson" data={shape}>
              <Layer
                id="answer-area-fill"
                type="fill"
                paint={{ "fill-color": "#d99447", "fill-opacity": 0.2 }}
              />
              <Layer
                id="answer-area-line"
                type="line"
                paint={{ "line-color": "#d99447", "line-width": 2 }}
              />
            </Source>
          )}

          {/* Вершины видно и до третьей точки: иначе первые два клика
              выглядят так, будто карта их не заметила. */}
          {area.map((vertex, index) => (
            <Marker
              key={`${vertex.latitude}-${vertex.longitude}-${index}`}
              longitude={vertex.longitude}
              latitude={vertex.latitude}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#d99447",
                  border: "2px solid #fff",
                }}
              />
            </Marker>
          ))}
          {point && !drawing && (
            <Marker longitude={point.longitude} latitude={point.latitude} anchor="bottom">
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "#d99447",
                  border: "3px solid #0c0d11",
                  boxShadow: "0 0 0 2px #d99447",
                }}
              />
            </Marker>
          )}
        </Map>

        {/* Условия Tiles API: логотип в углу карты и ссылкой на Яндекс Карты.
            Отступы от края заложены в самом файле. */}
        {usesYandexTiles && (
          <a
            href="https://yandex.ru/maps"
            target="_blank"
            rel="noreferrer"
            style={{ position: "absolute", left: 0, bottom: 0, zIndex: 10 }}
          >
            <img src={yandexLogo} width={88} height={48} alt="Яндекс Карты" />
          </a>
        )}
      </div>

      {onAreaChange && (
        <Space wrap>
          <Segmented
            value={mode}
            options={[
              { label: "Точка", value: "point" },
              { label: "Область", value: "area" },
            ]}
            onChange={(next) => {
              const chosen = next as "point" | "area";
              setMode(chosen);
              // Переключение на точку стирает область: иначе сохранилась бы
              // невидимая на карте фигура, о которой модератор уже забыл.
              if (chosen === "point" && area.length > 0) onAreaChange([]);
            }}
          />

          {drawing && (
            <>
              <Button size="small" disabled={area.length === 0} onClick={() => onAreaChange(area.slice(0, -1))}>
                Убрать последнюю
              </Button>
              <Button size="small" disabled={area.length === 0} onClick={() => onAreaChange([])}>
                Очистить
              </Button>
              <Typography.Text type={area.length >= MIN_AREA_POINTS ? "success" : "warning"}>
                {area.length >= MIN_AREA_POINTS
                  ? `Вершин: ${area.length}. Центр области станет точкой ответа`
                  : `Отметьте минимум ${MIN_AREA_POINTS} точки по контуру области`}
              </Typography.Text>
            </>
          )}
        </Space>
      )}

      <Space wrap style={{ display: drawing ? "none" : undefined }}>
        <Space.Compact>
          <Typography.Text type="secondary" style={{ lineHeight: "32px", marginRight: 8 }}>
            Широта
          </Typography.Text>
          <InputNumber
            value={point?.latitude}
            min={-90}
            max={90}
            step={0.0001}
            style={{ width: 140 }}
            onChange={(latitude) =>
              latitude !== null && update({ latitude, longitude: point?.longitude ?? 0 })
            }
          />
        </Space.Compact>

        <Space.Compact>
          <Typography.Text type="secondary" style={{ lineHeight: "32px", marginRight: 8 }}>
            Долгота
          </Typography.Text>
          <InputNumber
            value={point?.longitude}
            min={-180}
            max={180}
            step={0.0001}
            style={{ width: 140 }}
            onChange={(longitude) =>
              longitude !== null && update({ latitude: point?.latitude ?? 0, longitude })
            }
          />
        </Space.Compact>

        {!point && (
          <Typography.Text type="warning">
            Кликните по карте, чтобы задать место съёмки
          </Typography.Text>
        )}
      </Space>
    </Space>
  );
}
